import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import type { TeamInvitationsResponseData } from "../../hooks/useInvitation";
import { useTracker } from "../../hooks/useTracker";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import Heading from "../Heading/Heading";
import Disclaimer from "../SignUp/Disclaimer/Disclaimer";
import {
  passwordSchema,
  ValidatedPasswordInput,
} from "../ValidatedPasswordInput/ValidatedPasswordInput";

import style from "./TeamInvitationSelfServeSignUp.module.css";

const schema = z
  .object({
    name: z.string().trim().min(1, { message: "Please enter your name." }),
    password: passwordSchema,
    passwordRepeat: z.string(),
    termsAndPrivacyAccepted: z.boolean().default(false),
  })
  .refine((data) => data.password !== "", {
    path: ["passwordRepeat"],
    message: "Please enter your password.",
  })
  .refine((data) => data.password === data.passwordRepeat, {
    path: ["passwordRepeat"],
    message: "Passwords do not match.",
  })
  .refine((data) => data.termsAndPrivacyAccepted, {
    path: ["termsAndPrivacyAccepted"],
    message: "Please agree to our Terms and Privacy Policy before proceeding.",
  });

export type TeamInvitationSignUpFormData = z.infer<typeof schema>;

interface TeamInvitationSelfServeSignUpProps {
  submit: (data: TeamInvitationSignUpFormData) => void;
  invitation?: TeamInvitationsResponseData;
  isSubmitting: boolean;
  errorMessage?: string;
}

const TeamInvitationSelfServeSignUp = (props: TeamInvitationSelfServeSignUpProps) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TeamInvitationSignUpFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      password: "",
      passwordRepeat: "",
      termsAndPrivacyAccepted: false,
    },
  });

  const tracker = useTracker();

  return (
    <div className={style.modalContainer}>
      <Heading type="h1">Welcome to Shapr3D</Heading>
      <div className={style.teamText}>
        Create an account for <b className={style.email}>{props.invitation?.email}</b> to join{" "}
        <b>{props.invitation?.teamName}</b>.
      </div>
      <form className={style.form} onSubmit={handleSubmit(props.submit)}>
        <TextField
          errorMessage={errors.name?.message}
          type="text"
          placeholder="Your name"
          error={Boolean(errors.name)}
          disabled={props.isSubmitting}
          maxLength={200}
          {...register("name")}
        />

        <ValidatedPasswordInput
          type="password"
          placeholder="Password"
          disabled={props.isSubmitting}
          error={Boolean(errors.password)}
          {...register("password")}
        />

        <TextField
          errorMessage={errors.passwordRepeat?.message}
          type="password"
          placeholder="Re-enter password"
          disabled={props.isSubmitting}
          error={Boolean(errors.passwordRepeat)}
          {...register("passwordRepeat")}
        />

        <div className={style.disclaimer}>
          <Controller
            name="termsAndPrivacyAccepted"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Disclaimer areTermsAccepted={value} handleToggleTermsAccepted={onChange} />
            )}
          />

          {errors.termsAndPrivacyAccepted && (
            <span className={style.disclaimerErrorMessage}>
              {errors.termsAndPrivacyAccepted.message}
            </span>
          )}
        </div>

        <Button
          type="submit"
          disabled={props.isSubmitting}
          className={style.button}
          onClick={() => {
            tracker.trackButtonClick({
              action: ElementClickAction.JOIN_TEAM,
            });
          }}
        >
          Join now
        </Button>
        {props.errorMessage && <span className={style.error}>{props.errorMessage}</span>}
      </form>
    </div>
  );
};

export default TeamInvitationSelfServeSignUp;
